import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useAssetContext } from '@/context/AssetContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { 
  Clock, 
  ChevronRight, 
  ChevronLeft,
  CheckCircle,
  XCircle,
  AlertCircle,
  BookOpen,
  Send,
  Mic,
  MicOff,
  Square,
  ChevronDown,
  Headphones,
  PlayCircle,
  Timer
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

// Custom Dropdown Component for Fill-in-the-Blank questions
// Uses buttons instead of native <select> for better dark mode support
const DropdownQuestion = ({ question, selectedAnswers, setSelectedAnswers, isDark }) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  
  const handleSelect = (idx, value) => {
    const newAnswers = [...selectedAnswers];
    newAnswers[idx] = value;
    setSelectedAnswers(newAnswers);
    setOpenDropdown(null);
  };

  return (
    <div className="space-y-6">
      {/* Instructions */}
      <div className={`p-4 rounded-lg border-l-4 border-blue-500 ${isDark ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
        <p className={`font-medium ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
          📝 Select the correct word for each blank from the dropdown menus below
        </p>
      </div>
      
      {/* Display passage with blanks highlighted */}
      {question.content?.passage && (
        <div className={`p-4 rounded-lg ${isDark ? 'bg-[#1a2830]' : 'bg-gray-50'}`}>
          <p className={`leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            {question.content.passage.split(/\[BLANK\d+\]/).map((part, i, arr) => (
              <span key={i}>
                {part}
                {i < arr.length - 1 && (
                  <span className={`inline-block mx-1 px-2 py-0.5 rounded font-semibold ${
                    selectedAnswers[i] 
                      ? 'bg-green-500/20 text-green-500' 
                      : 'bg-amber-500/20 text-amber-500'
                  }`}>
                    {selectedAnswers[i] || `[BLANK${i + 1}]`}
                  </span>
                )}
              </span>
            ))}
          </p>
        </div>
      )}
      
      {/* Custom Dropdown selections */}
      <div className="space-y-4">
        <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
          Select your answers:
        </h4>
        {question.content?.blanks?.map((blank, idx) => (
          <div 
            key={blank.id} 
            className={`flex items-center gap-4 p-4 rounded-lg border-2 transition-all ${
              selectedAnswers[idx] 
                ? 'border-green-500/50 bg-green-500/5' 
                : isDark 
                  ? 'border-white/10 bg-[#1a2830]' 
                  : 'border-gray-200 bg-gray-50'
            }`}
          >
            <span className={`w-24 flex-shrink-0 font-bold text-sm px-3 py-1 rounded-full text-center ${
              selectedAnswers[idx]
                ? 'bg-green-500 text-white'
                : isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'
            }`}>
              {blank.id}
            </span>
            
            {/* Custom Dropdown */}
            <div className="relative flex-1">
              <button
                type="button"
                data-testid={`dropdown-${blank.id}`}
                onClick={() => setOpenDropdown(openDropdown === idx ? null : idx)}
                className={`w-full p-3 pr-10 rounded-lg border-2 text-left font-medium transition-all focus:ring-2 focus:ring-blue-500 focus:outline-none flex items-center justify-between ${
                  selectedAnswers[idx]
                    ? isDark 
                      ? 'bg-[#233842] border-green-500/50 text-green-400' 
                      : 'bg-white border-green-500 text-green-700'
                    : isDark 
                      ? 'bg-[#233842] border-amber-500/50 text-amber-400 hover:border-amber-400' 
                      : 'bg-white border-amber-400 text-amber-700 hover:border-amber-500'
                }`}
              >
                <span>{selectedAnswers[idx] || '⬇️ Click to select an option...'}</span>
                <ChevronDown className={`w-5 h-5 transition-transform ${openDropdown === idx ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {openDropdown === idx && (
                <div className={`absolute z-50 w-full mt-1 rounded-lg border-2 shadow-lg overflow-hidden ${
                  isDark 
                    ? 'bg-[#1a2830] border-white/20' 
                    : 'bg-white border-gray-200'
                }`}>
                  {blank.options?.map((opt, optIdx) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => handleSelect(idx, opt)}
                      className={`w-full px-4 py-3 text-left font-medium transition-colors ${
                        selectedAnswers[idx] === opt
                          ? isDark
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-green-100 text-green-700'
                          : isDark
                            ? 'text-white hover:bg-white/10'
                            : 'text-gray-800 hover:bg-gray-100'
                      } ${optIdx !== blank.options.length - 1 ? (isDark ? 'border-b border-white/10' : 'border-b border-gray-100') : ''}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {selectedAnswers[idx] && (
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
            )}
          </div>
        ))}
      </div>
      
      {/* Progress indicator */}
      <div className={`text-center text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
        {selectedAnswers.filter(a => a).length} of {question.content?.blanks?.length || 0} blanks filled
      </div>
    </div>
  );
};

const TestSessionPage = () => {
  const { testType, section } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { getLogo } = useAssetContext();
  
  const [session, setSession] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [selectedAnswers, setSelectedAnswers] = useState([]); // For MCQ multiple
  const [writingText, setWritingText] = useState('');
  const [timeLeft, setTimeLeft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState([]);
  const [testComplete, setTestComplete] = useState(false);
  const [finalResults, setFinalResults] = useState(null);
  
  // Cache for loaded questions to prevent refetching
  const [questionCache, setQuestionCache] = useState({});
  
  // Recording state for speaking
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  
  // Audio playback state for listening questions
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioPlayed, setAudioPlayed] = useState(false);
  
  // Ref to prevent double initialization
  const isInitializedRef = useRef(false);
  // Ref to track if timer warning has been shown
  const timerWarningShownRef = useRef(false);

  // Start test session - only if we don't have an active session
  useEffect(() => {
    // Guard: Prevent double initialization (React strict mode)
    if (isInitializedRef.current) return;
    if (session) return;
    
    isInitializedRef.current = true;
    startTest();
  }, [testType, section]);
  
  // Cleanup: Log session changes for debugging
  useEffect(() => {
    if (session) {
      console.log('Active session:', session.id, 'Questions:', session.questions);
    }
  }, [session]);

  // Timer countdown
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0 || testComplete) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        // Show warning at 30 seconds if not shown yet
        if (prev === 31 && !timerWarningShownRef.current) {
          timerWarningShownRef.current = true;
          toast.warning('⏰ 30 seconds remaining!', {
            description: 'Your test will auto-submit soon. Please finish your current answer.',
            duration: 5000,
            position: 'top-center'
          });
        }
        
        // Show final warning at 10 seconds
        if (prev === 11 && timerWarningShownRef.current) {
          toast.error('🚨 10 seconds remaining!', {
            description: 'Test will auto-submit now!',
            duration: 3000,
            position: 'top-center'
          });
        }
        
        if (prev <= 1) {
          clearInterval(timer);
          handleTimeUp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [timeLeft, testComplete]);

  const startTest = async () => {
    try {
      const res = await fetch(`${API_URL}/api/english-test/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ test_type: testType, section })
      });
      const data = await res.json();
      
      if (data.success) {
        setSession(data.session);
        setTotalQuestions(data.session.questions.length);
        await fetchQuestion(data.session.id, 0);
        
        // Set timer based on section
        const sectionTimes = {
          reading: 15 * 60,
          listening: 15 * 60,
          writing: 20 * 60,
          speaking: 10 * 60,
          speaking_writing: 20 * 60
        };
        setTimeLeft(sectionTimes[section] || 15 * 60);
      } else {
        alert(data.message || 'Failed to start test');
        navigate('/uac-desk/english-test');
      }
    } catch (error) {
      console.error('Error starting test:', error);
      alert('Failed to start test. Please try again.');
      navigate('/uac-desk/english-test');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestion = async (sessionId, index) => {
    try {
      // Check cache first
      const cacheKey = `${sessionId}-${index}`;
      if (questionCache[cacheKey]) {
        console.log(`Using cached question for index ${index}`);
        setCurrentQuestion(questionCache[cacheKey]);
        setCurrentIndex(index);
        setSelectedAnswer(null);
        setSelectedAnswers([]);
        setWritingText('');
        setAudioBlob(null);
        // Reset audio state for listening questions
        setAudioUrl(null);
        setAudioLoading(false);
        setAudioPlayed(false);
        return;
      }
      
      console.log(`Fetching question ${index} for session ${sessionId}`);
      const res = await fetch(`${API_URL}/api/english-test/session/${sessionId}/question/${index}`);
      const data = await res.json();
      console.log(`Fetch question response:`, data.success, data.question?.question_type);
      
      if (data.success) {
        // Cache the question
        setQuestionCache(prev => ({
          ...prev,
          [cacheKey]: data.question
        }));
        
        setCurrentQuestion(data.question);
        setCurrentIndex(index);
        setSelectedAnswer(null);
        setSelectedAnswers([]);
        setWritingText('');
        setAudioBlob(null);
        // Reset audio state for listening questions
        setAudioUrl(null);
        setAudioLoading(false);
        setAudioPlayed(false);
      } else {
        // Question not found - might be deleted
        console.error('Question fetch failed:', data);
        if (data.detail && data.detail.includes('stale data')) {
          alert('This test session has outdated data. Please start a new test.');
          navigate('/uac-desk/english-test');
        }
      }
    } catch (error) {
      console.error('Error fetching question:', error);
    }
  };

  const handleTimeUp = () => {
    completeTest();
  };

  const submitAnswer = async () => {
    if (!session || !currentQuestion) return;
    setSubmitting(true);
    
    // Add timeout safeguard - reset submitting after 30 seconds max
    const submitTimeout = setTimeout(() => {
      console.error('Submit timeout - resetting state');
      setSubmitting(false);
    }, 30000);
    
    try {
      let answer = selectedAnswer;
      
      // Handle different question types
      if (currentQuestion.question_type === 'mcq_multiple' || 
          currentQuestion.question_type === 'reorder_paragraphs') {
        answer = selectedAnswers;
      } else if (currentQuestion.question_type === 'fill_blank_dropdown') {
        // Convert array of selected values to object format
        const blanks = currentQuestion.content?.blanks || [];
        const answerObj = {};
        blanks.forEach((blank, idx) => {
          answerObj[blank.id] = selectedAnswers[idx] || '';
        });
        answer = answerObj;
      } else if (['essay', 'summarize_text'].includes(currentQuestion.question_type)) {
        // Submit writing
        const res = await fetch(`${API_URL}/api/english-test/submit-writing`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: session.id,
            question_id: currentQuestion.id,
            essay_text: writingText
          })
        });
        const data = await res.json();
        setResults(prev => [...prev, { questionId: currentQuestion.id, ...data }]);
        
        if (currentIndex < totalQuestions - 1) {
          await fetchQuestion(session.id, currentIndex + 1);
        } else {
          await completeTest();
        }
        setSubmitting(false);
        return;
      }
      
      // Submit regular answer
      console.log('Submitting answer:', { session_id: session.id, question_id: currentQuestion.id, answer });
      const res = await fetch(`${API_URL}/api/english-test/submit-answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: session.id,
          question_id: currentQuestion.id,
          user_answer: answer
        })
      });
      const data = await res.json();
      console.log('Submit answer response:', data);
      
      if (!data.success && data.detail) {
        console.error('Submit failed:', data.detail);
      }
      
      setResults(prev => [...prev, { questionId: currentQuestion.id, ...data }]);
      
      // Move to next question or complete
      if (currentIndex < totalQuestions - 1) {
        try {
          await fetchQuestion(session.id, currentIndex + 1);
        } catch (fetchError) {
          console.error('Error fetching next question:', fetchError);
        }
      } else {
        console.log('Last question, completing test...');
        await completeTest();
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
    } finally {
      clearTimeout(submitTimeout);
      setSubmitting(false);
    }
  };

  const completeTest = async () => {
    try {
      console.log('Completing test with session ID:', session.id);
      const res = await fetch(`${API_URL}/api/english-test/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: session.id })
      });
      const data = await res.json();
      console.log('Complete test response:', data);
      
      if (data.success) {
        setFinalResults(data);
        setTestComplete(true);
      } else {
        console.error('Test completion failed:', data);
        alert('Failed to complete test: ' + (data.detail || data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error completing test:', error);
      alert('Error completing test. Please try again.');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleMultipleSelect = (optionId) => {
    setSelectedAnswers(prev => {
      if (prev.includes(optionId)) {
        return prev.filter(id => id !== optionId);
      }
      return [...prev, optionId];
    });
  };

  // Recording functions for speaking
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setAudioBlob(blob);
      };
      
      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen pt-20 flex items-center justify-center ${isDark ? 'bg-[#1a2830]' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Loading test...</p>
        </div>
      </div>
    );
  }

  // Test Complete - Show Results
  if (testComplete && finalResults) {
    const score = finalResults.overall_score;
    const feedback = finalResults.feedback;
    
    // Check if there's AI writing evaluation in the results
    const writingResult = results.find(r => r.evaluation);
    const aiEval = writingResult?.evaluation;
    
    // Use AI band score for writing if available
    const displayBand = aiEval?.overall_band || aiEval?.band_scores?.task_response || score.band_score;
    const displayPoints = aiEval?.overall_score || score.points;
    
    // Merge AI feedback with general feedback
    const mergedFeedback = {
      strengths: aiEval?.strengths?.length ? aiEval.strengths : feedback?.strengths || [],
      weaknesses: aiEval?.weaknesses?.length ? aiEval.weaknesses : feedback?.weaknesses || [],
      tips: aiEval?.tips?.length ? aiEval.tips : feedback?.tips || [],
      grammar_corrections: aiEval?.grammar_corrections || [],
      vocabulary_suggestions: aiEval?.vocabulary_suggestions || []
    };
    
    return (
      <div className={`min-h-screen w-full max-w-[100vw] overflow-x-hidden pt-20 pb-12 ${isDark ? 'bg-[#1a2830]' : 'bg-gray-50'}`}>
        <div className="w-full max-w-3xl mx-auto px-3 sm:px-4">
          {/* Logo on Results Page */}
          <div className="flex justify-center mb-6">
            <img 
              src={getLogo()} 
              alt="UniXperts" 
              className={`h-12 object-contain ${isDark ? 'brightness-0 invert' : ''}`}
            />
          </div>
          <Card className={isDark ? 'bg-[#233842] border-white/10' : ''}>
            <CardHeader className="text-center">
              <div className="mb-4">
                <CheckCircle className="w-16 h-16 mx-auto text-green-500" />
              </div>
              <CardTitle className={`text-2xl ${isDark ? 'text-white' : ''}`}>
                Test Complete!
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Score Display */}
              <div className={`text-center p-6 rounded-xl mb-6 ${isDark ? 'bg-[#1a2830]' : 'bg-gray-100'}`}>
                <div className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Your Estimated Score
                </div>
                <div className={`text-5xl font-bold mb-2 ${
                  (displayBand >= 7 || displayPoints >= 65) 
                    ? 'text-green-500' 
                    : (displayBand >= 6 || displayPoints >= 50) 
                    ? 'text-amber-500' 
                    : 'text-red-500'
                }`}>
                  {displayBand ? `Band ${displayBand}` : displayPoints ? `${displayPoints} / 90` : `Band ${score.band_score || 2.5}`}
                </div>
                {aiEval ? (
                  <div className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                    Word count: {aiEval.word_count || 0}
                  </div>
                ) : (
                  <div className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                    {Math.round(score.percentage)}% accuracy ({Math.round(score.raw_score)}/{score.max_score} correct)
                  </div>
                )}
              </div>
              
              {/* AI Band Score Breakdown for Writing */}
              {aiEval?.band_scores && (
                <div className={`mb-6 p-4 rounded-lg ${isDark ? 'bg-[#1a2830]' : 'bg-gray-50'}`}>
                  <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : ''}`}>Score Breakdown</h3>
                  <div className="space-y-2">
                    {Object.entries(aiEval.band_scores).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between">
                        <span className={`capitalize ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {key.replace(/_/g, ' ')}
                        </span>
                        <span className={`font-bold ${value >= 7 ? 'text-green-500' : value >= 5 ? 'text-amber-500' : 'text-red-500'}`}>
                          {value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Feedback */}
              <div className="space-y-4">
                {mergedFeedback.strengths?.length > 0 && (
                  <div>
                    <h3 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                      <CheckCircle className="w-5 h-5 text-green-500" /> Strengths
                    </h3>
                    <ul className={`list-disc list-inside space-y-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {mergedFeedback.strengths.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
                
                {mergedFeedback.weaknesses?.length > 0 && (
                  <div>
                    <h3 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                      <AlertCircle className="w-5 h-5 text-amber-500" /> Areas to Improve
                    </h3>
                    <ul className={`list-disc list-inside space-y-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {mergedFeedback.weaknesses.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </div>
                )}
                
                {mergedFeedback.grammar_corrections?.length > 0 && (
                  <div>
                    <h3 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                      <XCircle className="w-5 h-5 text-red-500" /> Grammar Corrections
                    </h3>
                    <div className="space-y-2">
                      {mergedFeedback.grammar_corrections.map((gc, i) => (
                        <div key={i} className={`p-3 rounded-lg ${isDark ? 'bg-[#1a2830]' : 'bg-gray-50'}`}>
                          <div className="text-red-500 line-through">{gc.original}</div>
                          <div className="text-green-500">{gc.corrected}</div>
                          <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{gc.explanation}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {mergedFeedback.vocabulary_suggestions?.length > 0 && (
                  <div>
                    <h3 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                      <BookOpen className="w-5 h-5 text-purple-500" /> Vocabulary Suggestions
                    </h3>
                    <div className="space-y-2">
                      {mergedFeedback.vocabulary_suggestions.map((vs, i) => (
                        <div key={i} className={`flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          <span className="text-amber-500">"{vs.word}"</span>
                          <span>→</span>
                          <span className="text-green-500">{vs.alternatives?.join(', ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {mergedFeedback.tips?.length > 0 && (
                  <div>
                    <h3 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                      <BookOpen className="w-5 h-5 text-blue-500" /> Tips
                    </h3>
                    <ul className={`list-disc list-inside space-y-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {mergedFeedback.tips.map((t, i) => <li key={i}>{t}</li>)}
                    </ul>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-3 mt-8">
                <Button 
                  className="w-full"
                  onClick={() => navigate('/uac-desk/english-test/results')}
                  data-testid="view-all-results-btn"
                >
                  View All Results
                </Button>
                <div className="flex gap-4">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => navigate('/uac-desk/english-test')}
                  >
                    Back to Tests
                  </Button>
                  <Button 
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setTestComplete(false);
                      setFinalResults(null);
                      setResults([]);
                      setCurrentIndex(0);
                      timerWarningShownRef.current = false;
                      startTest();
                    }}
                  >
                    Try Again
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!currentQuestion) {
    return (
      <div className={`min-h-screen pt-20 flex items-center justify-center ${isDark ? 'bg-[#1a2830]' : 'bg-gray-50'}`}>
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
          <p className={isDark ? 'text-white' : 'text-gray-900'}>No questions available</p>
          <Button className="mt-4" onClick={() => navigate('/uac-desk/english-test')}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const isWritingQuestion = ['essay', 'summarize_text', 'graph_description'].includes(currentQuestion.question_type);
  const isSpeakingQuestion = ['speaking_part1', 'speaking_part2', 'speaking_prompt', 'read_aloud', 'repeat_sentence', 'describe_image', 'answer_short'].includes(currentQuestion.question_type);
  const isListeningQuestion = ['listening_mcq', 'listening_fill_blank', 'listening_highlight_summary', 'highlight_summary', 'write_dictation', 'mcq_single'].includes(currentQuestion.question_type);
  const isMultipleChoice = currentQuestion.question_type === 'mcq_multiple';
  
  // Generate audio for listening questions
  const generateAudio = async () => {
    // Try audio_text first, then audio_transcript for dictation, then passage for AI-generated questions
    const audioText = currentQuestion.content?.audio_text || currentQuestion.content?.audio_transcript || currentQuestion.content?.passage;
    console.log('generateAudio called, audioText:', audioText ? audioText.substring(0, 50) + '...' : 'null');
    console.log('currentQuestion.content:', currentQuestion.content);
    
    if (!audioText) {
      console.error('No audio text available for this question');
      return;
    }
    
    setAudioLoading(true);
    try {
      console.log('Calling generate-audio API...');
      const res = await fetch(`${API_URL}/api/english-test/generate-audio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: audioText,
          voice: 'nova',
          speed: 0.9
        })
      });
      console.log('API response status:', res.status);
      const data = await res.json();
      console.log('API response data:', data.success, data.audio ? 'has audio' : 'no audio');
      if (data.success) {
        setAudioUrl(`data:audio/mp3;base64,${data.audio}`);
      } else {
        console.error('Failed to generate audio:', data);
      }
    } catch (error) {
      console.error('Error generating audio:', error);
    } finally {
      setAudioLoading(false);
    }
  };

  return (
    <div className={`min-h-screen w-full max-w-[100vw] overflow-x-hidden pt-16 pb-24 ${isDark ? 'bg-[#1a2830]' : 'bg-gray-50'}`}>
      <div className="w-full max-w-4xl mx-auto px-3 sm:px-4">
        {/* Header */}
        <div className={`sticky top-16 z-10 py-4 ${isDark ? 'bg-[#1a2830]' : 'bg-gray-50'}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              {/* Logo */}
              <img 
                src={getLogo()} 
                alt="UniXperts" 
                className={`h-8 object-contain ${isDark ? 'brightness-0 invert' : ''}`}
              />
              <div className={`h-6 w-px ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} />
              <Button
                variant="outline"
                size="sm"
                data-testid="exit-test-btn"
                onClick={() => {
                  if (window.confirm('Are you sure you want to exit? Your progress will be lost.')) {
                    navigate('/uac-desk/english-test');
                  }
                }}
                className={`border-red-500/50 text-red-500 hover:bg-red-500/10 hover:text-red-400 hover:border-red-400`}
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Exit Test
              </Button>
              <Badge className={isDark ? 'bg-blue-500/20 text-blue-400' : ''}>
                {testType.toUpperCase()} - {section.charAt(0).toUpperCase() + section.slice(1).replace('_', ' & ')}
              </Badge>
            </div>
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
              timeLeft < 60 ? 'bg-red-500/20 text-red-500' : 
              timeLeft < 300 ? 'bg-amber-500/20 text-amber-500' : 
              isDark ? 'bg-[#233842] text-white' : 'bg-gray-200 text-gray-700'
            }`}>
              <Clock className="w-4 h-4" />
              <span className="font-mono font-bold">{formatTime(timeLeft)}</span>
            </div>
          </div>
          <Progress value={(currentIndex / totalQuestions) * 100} className="h-2" />
          <div className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Question {currentIndex + 1} of {totalQuestions}
          </div>
        </div>

        {/* Question Content */}
        <Card className={`mt-4 ${isDark ? 'bg-[#233842] border-white/10' : ''}`}>
          <CardContent className="p-6">
            {/* Passage (if exists) */}
            {currentQuestion.content?.passage && (
              <div className={`mb-6 p-4 rounded-lg ${isDark ? 'bg-[#1a2830]' : 'bg-gray-50'}`}>
                <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : ''}`}>Reading Passage:</h3>
                <div className={`prose prose-sm max-w-none ${isDark ? 'prose-invert' : ''}`}>
                  {currentQuestion.content.passage.split('\n\n').map((para, i) => (
                    <p key={i} className={isDark ? 'text-gray-300' : 'text-gray-700'}>{para}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Question (but NOT for listening - listening shows its own question) */}
            {!isListeningQuestion && (
              <div className="mb-6">
                {currentQuestion.instructions && (
                  <p className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {currentQuestion.instructions}
                  </p>
                )}
                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : ''}`}>
                  {currentQuestion.content?.text}
                </h2>
              </div>
            )}

            {/* Answer Options - MCQ Single (but NOT for listening - listening has its own section) */}
            {currentQuestion.content?.options && !isMultipleChoice && !isListeningQuestion && (
              <div className="space-y-3">
                {currentQuestion.content.options.map((option) => (
                  <div
                    key={option.id}
                    onClick={() => setSelectedAnswer(option.id)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedAnswer === option.id
                        ? 'border-blue-500 bg-blue-500/10'
                        : isDark 
                          ? 'border-white/10 hover:border-white/20' 
                          : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        selectedAnswer === option.id
                          ? 'border-blue-500 bg-blue-500'
                          : isDark ? 'border-gray-500' : 'border-gray-300'
                      }`}>
                        {selectedAnswer === option.id && (
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        )}
                      </div>
                      <span className={isDark ? 'text-white' : ''}>{option.text}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Answer Options - MCQ Multiple */}
            {isMultipleChoice && currentQuestion.content?.options && (
              <div className="space-y-3">
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Select all that apply
                </p>
                {currentQuestion.content.options.map((option) => (
                  <div
                    key={option.id}
                    onClick={() => handleMultipleSelect(option.id)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedAnswers.includes(option.id)
                        ? 'border-blue-500 bg-blue-500/10'
                        : isDark 
                          ? 'border-white/10 hover:border-white/20' 
                          : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                        selectedAnswers.includes(option.id)
                          ? 'border-blue-500 bg-blue-500'
                          : isDark ? 'border-gray-500' : 'border-gray-300'
                      }`}>
                        {selectedAnswers.includes(option.id) && (
                          <CheckCircle className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <span className={isDark ? 'text-white' : ''}>{option.text}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Fill in the Blank */}
            {currentQuestion.question_type === 'fill_blank' && (
              <div>
                <input
                  type="text"
                  value={selectedAnswer || ''}
                  onChange={(e) => setSelectedAnswer(e.target.value)}
                  placeholder="Type your answer..."
                  spellCheck={false}
                  autoCorrect="off"
                  autoCapitalize="off"
                  autoComplete="off"
                  className={`w-full p-4 rounded-lg border-2 ${
                    isDark 
                      ? 'bg-[#1a2830] border-white/10 text-white placeholder-gray-500' 
                      : 'border-gray-200'
                  }`}
                />
              </div>
            )}

            {/* Reorder Paragraphs - Simplified click-to-order interface */}
            {currentQuestion.question_type === 'reorder_paragraphs' && currentQuestion.content?.paragraphs && (
              <div className="space-y-3">
                <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Click the sentences in the correct order (1st, 2nd, 3rd...)
                </p>
                {currentQuestion.content.paragraphs.map((para, idx) => {
                  const orderIndex = selectedAnswers.indexOf(para.id);
                  return (
                    <div
                      key={para.id}
                      onClick={() => {
                        if (orderIndex >= 0) {
                          // Remove from selection
                          setSelectedAnswers(prev => prev.filter(id => id !== para.id));
                        } else {
                          // Add to selection
                          setSelectedAnswers(prev => [...prev, para.id]);
                        }
                      }}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        orderIndex >= 0
                          ? 'border-blue-500 bg-blue-500/10'
                          : isDark 
                            ? 'border-white/10 hover:border-white/20' 
                            : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          orderIndex >= 0
                            ? 'bg-blue-500 text-white'
                            : isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                        }`}>
                          {orderIndex >= 0 ? orderIndex + 1 : '?'}
                        </div>
                        <span className={isDark ? 'text-white' : ''}>{para.text}</span>
                      </div>
                    </div>
                  );
                })}
                {selectedAnswers.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedAnswers([])}
                    className="mt-2"
                  >
                    Clear Selection
                  </Button>
                )}
              </div>
            )}

            {/* Fill in Blank - Dropdown with improved UI */}
            {currentQuestion.question_type === 'fill_blank_dropdown' && (
              <DropdownQuestion 
                question={currentQuestion}
                selectedAnswers={selectedAnswers}
                setSelectedAnswers={setSelectedAnswers}
                isDark={isDark}
              />
            )}

            {/* Writing Task */}
            {isWritingQuestion && (
              <div>
                <Textarea
                  value={writingText}
                  onChange={(e) => setWritingText(e.target.value)}
                  placeholder="Write your response here..."
                  spellCheck={false}
                  autoCorrect="off"
                  autoCapitalize="off"
                  autoComplete="off"
                  data-gramm="false"
                  data-gramm_editor="false"
                  data-enable-grammarly="false"
                  className={`min-h-[300px] ${
                    isDark ? 'bg-[#1a2830] border-white/10 text-white' : ''
                  }`}
                />
                <div className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Word count: {writingText.split(/\s+/).filter(w => w).length}
                  {currentQuestion.question_type === 'essay' && ' (minimum 250 words)'}
                  {currentQuestion.question_type === 'summarize_text' && ' (5-75 words)'}
                </div>
              </div>
            )}

            {/* Listening Task - Audio Player */}
            {isListeningQuestion && (
              <div className="space-y-6">
                {/* Audio Player Section */}
                <div className={`p-6 rounded-xl ${isDark ? 'bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/20' : 'bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200'}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`p-3 rounded-full ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                      <Headphones className={`w-6 h-6 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                    </div>
                    <div>
                      <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Listen to the Recording
                      </h3>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {audioPlayed ? 'You can replay the audio if needed' : 'Click play to listen to the audio passage'}
                      </p>
                    </div>
                  </div>
                  
                  {!audioUrl && !audioLoading && (
                    <Button 
                      onClick={generateAudio} 
                      size="lg" 
                      className="w-full bg-purple-600 hover:bg-purple-700"
                    >
                      <PlayCircle className="w-5 h-5 mr-2" /> Generate Audio
                    </Button>
                  )}
                  
                  {audioLoading && (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500 mr-3"></div>
                      <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Generating audio...</span>
                    </div>
                  )}
                  
                  {audioUrl && (
                    <div className="space-y-3">
                      <audio 
                        controls 
                        src={audioUrl} 
                        onPlay={() => setAudioPlayed(true)}
                        className="w-full" 
                        style={{ 
                          borderRadius: '12px',
                          background: isDark ? '#1a2830' : '#f3f4f6'
                        }}
                      />
                      {audioPlayed && (
                        <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                          <CheckCircle className="w-4 h-4" />
                          Audio played - Now answer the question below
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Question Section */}
                <div className={`p-4 rounded-lg border-l-4 border-purple-500 ${isDark ? 'bg-[#1a2830]' : 'bg-gray-50'}`}>
                  {/* Show instruction text */}
                  {currentQuestion.content?.text && (
                    <p className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {currentQuestion.content.text}
                    </p>
                  )}
                  {/* Show actual question if it exists */}
                  {currentQuestion.content?.question && (
                    <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {currentQuestion.content.question}
                    </h4>
                  )}
                  {/* Fallback: if no separate question field, use text as the question */}
                  {!currentQuestion.content?.question && currentQuestion.content?.text && currentQuestion.content.text.includes('?') && (
                    <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {currentQuestion.content.text}
                    </h4>
                  )}
                </div>
                
                {/* MCQ Options for Listening */}
                {currentQuestion.content?.options && (
                  <div className="space-y-3">
                    {currentQuestion.content.options.map((option) => (
                      <div
                        key={option.id}
                        onClick={() => setSelectedAnswer(option.id)}
                        data-testid={`listening-option-${option.id}`}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedAnswer === option.id
                            ? 'border-purple-500 bg-purple-500/10'
                            : isDark 
                              ? 'border-white/10 hover:border-white/20' 
                              : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                            selectedAnswer === option.id
                              ? 'border-purple-500 bg-purple-500'
                              : isDark ? 'border-gray-500' : 'border-gray-300'
                          }`}>
                            {selectedAnswer === option.id && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                          <span className={isDark ? 'text-white' : ''}>{option.text}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Fill in the Blank for Listening */}
                {currentQuestion.question_type === 'listening_fill_blank' && !currentQuestion.content?.options && (
                  <div className="space-y-4">
                    {/* Show the sentence with blank - only if it contains actual blanks (underscores) */}
                    {(() => {
                      const sentenceWithBlank = currentQuestion.content?.prompt_with_blanks || currentQuestion.content?.text;
                      // Only show if it contains underscores (actual blanks)
                      if (sentenceWithBlank && sentenceWithBlank.includes('_')) {
                        return (
                          <div className={`p-4 rounded-lg border-l-4 border-purple-500 ${isDark ? 'bg-purple-500/10' : 'bg-purple-50'}`}>
                            <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-800'}`}>
                              {sentenceWithBlank}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    <input
                      type="text"
                      value={selectedAnswer || ''}
                      onChange={(e) => setSelectedAnswer(e.target.value)}
                      placeholder="Type the missing word(s)..."
                      data-testid="listening-fill-blank"
                      spellCheck={false}
                      autoCorrect="off"
                      autoCapitalize="off"
                      autoComplete="off"
                      className={`w-full p-4 rounded-lg border-2 ${
                        isDark 
                          ? 'bg-[#1a2830] border-white/10 text-white placeholder-gray-500' 
                          : 'border-gray-200'
                      }`}
                    />
                  </div>
                )}

                {/* Write from Dictation */}
                {currentQuestion.question_type === 'write_dictation' && (
                  <div className="space-y-4">
                    <div className={`p-4 rounded-lg border-l-4 border-cyan-500 ${isDark ? 'bg-cyan-500/10' : 'bg-cyan-50'}`}>
                      <p className={`font-medium ${isDark ? 'text-cyan-400' : 'text-cyan-700'}`}>
                        ✍️ Type the sentence exactly as you hear it
                      </p>
                    </div>
                    <textarea
                      value={selectedAnswer || ''}
                      onChange={(e) => setSelectedAnswer(e.target.value)}
                      placeholder="Type the sentence you heard..."
                      data-testid="write-dictation"
                      spellCheck={false}
                      autoCorrect="off"
                      autoCapitalize="off"
                      autoComplete="off"
                      rows={3}
                      className={`w-full p-4 rounded-lg border-2 resize-none ${
                        isDark 
                          ? 'bg-[#1a2830] border-white/10 text-white placeholder-gray-500' 
                          : 'border-gray-200'
                      }`}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Speaking Task */}
            {isSpeakingQuestion && (
              <div className="space-y-6">
                {/* Instructions */}
                <div className={`p-4 rounded-lg border-l-4 border-orange-500 ${isDark ? 'bg-orange-500/10' : 'bg-orange-50'}`}>
                  <p className={`font-medium ${isDark ? 'text-orange-400' : 'text-orange-700'}`}>
                    🎤 {currentQuestion.question_type === 'read_aloud' 
                      ? 'Read the following text aloud clearly'
                      : currentQuestion.question_type === 'repeat_sentence'
                      ? 'Listen and repeat the sentence exactly'
                      : currentQuestion.question_type === 'describe_image'
                      ? 'Describe the image in detail'
                      : 'Speak your response naturally'}
                  </p>
                </div>
                
                {/* For repeat_sentence, play the audio first */}
                {(currentQuestion.question_type === 'repeat_sentence' || currentQuestion.question_type === 'answer_short') && currentQuestion.content?.audio_text && (
                  <div className={`p-4 rounded-lg ${isDark ? 'bg-[#1a2830]' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <Headphones className={`w-5 h-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                      <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Listen to the audio, then record your response
                      </span>
                    </div>
                    {!audioUrl && !audioLoading && (
                      <Button onClick={generateAudio} variant="outline" size="sm">
                        <PlayCircle className="w-4 h-4 mr-2" /> Play Audio
                      </Button>
                    )}
                    {audioLoading && (
                      <div className="flex items-center gap-2 text-sm">
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-purple-500"></div>
                        Loading audio...
                      </div>
                    )}
                    {audioUrl && (
                      <audio controls src={audioUrl} className="w-full mt-2" />
                    )}
                  </div>
                )}
                
                {/* Recording Interface */}
                <div className="text-center py-6">
                  <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full mb-4 ${
                    isRecording 
                      ? 'bg-red-500 animate-pulse' 
                      : isDark ? 'bg-[#1a2830] border-2 border-orange-500/30' : 'bg-gray-100 border-2 border-orange-200'
                  }`}>
                    {isRecording ? (
                      <Mic className="w-10 h-10 text-white" />
                    ) : (
                      <MicOff className={`w-10 h-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    )}
                  </div>
                  
                  <div className="space-x-4">
                    {!isRecording ? (
                      <Button onClick={startRecording} size="lg" className="bg-orange-500 hover:bg-orange-600">
                        <Mic className="w-5 h-5 mr-2" /> Start Recording
                      </Button>
                    ) : (
                      <Button onClick={stopRecording} variant="destructive" size="lg">
                        <Square className="w-5 h-5 mr-2" /> Stop Recording
                      </Button>
                    )}
                  </div>
                  
                  {audioBlob && (
                    <div className="mt-6 space-y-3">
                      <div className={`text-sm ${isDark ? 'text-green-400' : 'text-green-600'} flex items-center justify-center gap-2`}>
                        <CheckCircle className="w-4 h-4" />
                        Recording captured successfully
                      </div>
                      <audio controls src={URL.createObjectURL(audioBlob)} className="mx-auto" />
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Click "Next" to submit your recording for AI evaluation
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bottom Navigation */}
        <div className={`fixed bottom-0 left-0 right-0 p-4 ${isDark ? 'bg-[#233842]' : 'bg-white'} border-t ${isDark ? 'border-white/10' : ''}`}>
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => currentIndex > 0 && fetchQuestion(session.id, currentIndex - 1)}
              disabled={currentIndex === 0}
            >
              <ChevronLeft className="w-4 h-4 mr-2" /> Previous
            </Button>
            
            <Button
              onClick={submitAnswer}
              disabled={
                submitting || 
                (!selectedAnswer && selectedAnswers.length === 0 && !writingText && !audioBlob)
              }
            >
              {submitting ? (
                'Submitting...'
              ) : currentIndex === totalQuestions - 1 ? (
                <>Finish Test <CheckCircle className="w-4 h-4 ml-2" /></>
              ) : (
                <>Next <ChevronRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestSessionPage;
